import type { GridFilterItem, GridSortModel } from '@mui/x-data-grid-pro';

/**
 * Client-side filtering and sorting of the Campaign Leaks grids. Done here rather than by DataGridPro so the
 * bars, the KPI and the CSV export work on exactly the same rows as the table.
 */

export type Accessors<R> = Record<string, (row: R) => string | number | null | undefined>;

const isEmpty = (value: unknown) =>
  value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);

/** An item counts as an active filter once it has a value (a range needs a min or a max). */
export const isActiveFilter = (item: GridFilterItem) => {
  if (item.operator === 'between') {
    const [min, max] = Array.isArray(item.value) ? item.value : [];
    return !isEmpty(min) || !isEmpty(max);
  }
  return !isEmpty(item.value);
};

const matches = (cell: unknown, item: GridFilterItem): boolean => {
  switch (item.operator) {
    case 'contains':
      return String(cell ?? '').toLowerCase().includes(String(item.value).toLowerCase());
    case 'isAnyOf':
      return (item.value as unknown[]).includes(cell);
    case 'between': {
      const [min, max] = item.value as [number | string | null, number | string | null];
      const num = Number(cell ?? 0);
      if (!isEmpty(min) && num < Number(min)) return false;
      if (!isEmpty(max) && num > Number(max)) return false;
      return true;
    }
    default:
      return true;
  }
};

/** Every active filter must match (AND). */
export const applyFilters = <R>(rows: R[], items: GridFilterItem[], accessors: Accessors<R>) => {
  const active = items.filter(isActiveFilter);
  if (!active.length) return rows;
  return rows.filter((row) =>
    active.every((item) => {
      const accessor = accessors[item.field];
      return accessor ? matches(accessor(row), item) : true;
    })
  );
};

/** Sorts on the first sort item; null values always go last. */
export const applySort = <R>(rows: R[], sortModel: GridSortModel, accessors: Accessors<R>) => {
  const [sort] = sortModel;
  const accessor = sort && accessors[sort.field];
  if (!sort?.sort || !accessor) return rows;
  const direction = sort.sort === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = accessor(a);
    const vb = accessor(b);
    if (va === null || va === undefined) return vb === null || vb === undefined ? 0 : 1;
    if (vb === null || vb === undefined) return -1;
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * direction;
    return String(va).localeCompare(String(vb), undefined, { sensitivity: 'base' }) * direction;
  });
};
